import React from 'react';
import { Users, Info, Settings } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from './ui/tooltip';

/**
 * Capability Transfer Factors Card
 * Reusable component for configuring dynamic transfer rate factors
 */
export const TransferFactorsCard = ({ 
  config, 
  onConfigChange, 
  headcount = 1000,
  showLearnerPopulation = true,
  className = "",
  transferMethod = null,
  transferMethodLabel = null,
  roeCompositeDetail = null,
}) => {
  const managerSupport = config.manager_support || 'informed';
  const jobAlignment = config.job_alignment || 'role_relevant';
  const timeToApply = config.time_to_apply || '2_4_weeks';
  const learnerPopulation = config.learner_population;
  const maturity = config.current_maturity || 50;

  // Calculate maturity points for display
  const getMaturityPoints = (m) => {
    if (m <= 25) return 0;
    if (m <= 50) return 3;
    if (m <= 75) return 6;
    return 8;
  };

  return (
    <Card className={`border-purple-200 bg-gradient-to-br from-purple-50/50 to-violet-50/50 ${className}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-purple-600" />
          <CardTitle className="text-base font-semibold">Value Transfer Conditions</CardTitle>
          <Tooltip>
            <TooltipTrigger>
              <Info className="h-4 w-4 text-slate-400" />
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <p className="font-medium mb-1">About Value Transfer</p>
              <p className="text-xs">Research shows 10-60% of learning transfers without support, vs 70-90% with strong systems. These conditions predict transfer success.</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <CardDescription className="text-purple-600 mt-1">
          Conditions that influence how effectively capability converts into performance.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Adjustable Factors Hint - simplified, no icon */}
        <p className="text-xs text-purple-600 italic">
          Adjust these factors to match your program. Click dropdowns to customize.
        </p>

        {/* Dropdowns - stacked on mobile, row on larger screens */}
        <div className="space-y-3">
          {/* Manager Support */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Manager Support</Label>
              <span className="text-xs text-purple-600">(25% wt)</span>
            </div>
            <Select
              value={managerSupport}
              onValueChange={(value) => onConfigChange({ manager_support: value })}
            >
              <SelectTrigger 
                data-testid="transfer-manager-support"
                className="h-8 text-xs border border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (+0%)</SelectItem>
                <SelectItem value="aware">Aware (+5%)</SelectItem>
                <SelectItem value="informed">Informed (+5%)</SelectItem>
                <SelectItem value="supportive">Supportive (+10%)</SelectItem>
                <SelectItem value="coaching">Coaching (+14%)</SelectItem>
                <SelectItem value="accountable">Accountable (+18%)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Job Alignment */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Job Alignment</Label>
              <span className="text-xs text-purple-600">(20% wt)</span>
            </div>
            <Select
              value={jobAlignment}
              onValueChange={(value) => onConfigChange({ job_alignment: value })}
            >
              <SelectTrigger 
                data-testid="transfer-job-alignment"
                className="h-8 text-xs border border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="generic">Generic (+0%)</SelectItem>
                <SelectItem value="role_relevant">Role-Relevant (+5%)</SelectItem>
                <SelectItem value="job_specific">Job-Specific (+10%)</SelectItem>
                <SelectItem value="performance_integrated">Performance-Integrated (+15%)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Time to Apply */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Time to Apply</Label>
              <span className="text-xs text-purple-600">(20% wt)</span>
            </div>
            <Select
              value={timeToApply}
              onValueChange={(value) => onConfigChange({ time_to_apply: value })}
            >
              <SelectTrigger 
                data-testid="transfer-time-to-apply"
                className="h-8 text-xs border border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="over_3_months">&gt; 3 Months (-5%)</SelectItem>
                <SelectItem value="1_3_months">1-3 Months (+0%)</SelectItem>
                <SelectItem value="2_4_weeks">2-4 Weeks (+5%)</SelectItem>
                <SelectItem value="under_2_weeks">&lt; 2 Weeks (+8%)</SelectItem>
                <SelectItem value="immediate">Immediate (+10%)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Learner Population - simplified layout */}
        {showLearnerPopulation && (
          <div className="bg-amber-50/50 rounded-lg border border-amber-200 p-3 space-y-2 overflow-hidden">
            <div className="flex items-start gap-2">
              <Users className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-semibold text-amber-900">Learner Population</span>
                  <span className="px-1.5 py-0.5 text-xs font-bold text-amber-700 bg-amber-200 rounded-full">10%</span>
                </div>
                <p className="text-xs text-amber-600 mt-1">
                  How many employees will participate?
                </p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="flex-shrink-0 mt-0.5">
                    <Info className="h-3.5 w-3.5 text-amber-600" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  <p className="font-medium mb-1">Why this matters</p>
                  <p className="text-xs">Smaller, targeted cohorts typically receive more personalized support, leading to better learning transfer.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <Input
              type="number"
              placeholder={headcount.toLocaleString()}
              value={learnerPopulation || ''}
              onChange={(e) => onConfigChange({ 
                learner_population: e.target.value ? parseInt(e.target.value) : null 
              })}
              className="w-full max-w-[120px] h-8 text-sm font-semibold bg-white border border-amber-300 focus:border-amber-500 focus:ring-amber-500"
              data-testid="transfer-learner-population"
            />
            <p className="text-xs text-amber-600 italic">
              {learnerPopulation 
                ? `${((learnerPopulation / headcount) * 100).toFixed(0)}% of org`
                : `Using full headcount (${headcount.toLocaleString()})`
              }
            </p>
            {learnerPopulation && learnerPopulation < headcount * 0.3 && (
              <p className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded inline-block">
                ✓ Focused cohort
              </p>
            )}
          </div>
        )}

        {/* Auto-calculated factors info */}
        <div className="p-3 bg-purple-100/50 rounded-lg border border-purple-200">
          <p className="text-xs text-purple-800">
            <strong>Auto-calculated:</strong> L&D Maturity ({maturity}% = +{getMaturityPoints(maturity)}%) and Learning Modality from profile (15% wt)
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Transfer Rate Results Card
 * Displays the calculated transfer rate with factor breakdown
 */
export const TransferRateResultsCard = ({ transferRate, transferMethod, roeCompositeDetail, className = "" }) => {
  if (!transferRate || !transferRate.factors) return null;

  return (
    <div className={`p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-200 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-purple-600" />
          <div>
            <p className="text-sm font-medium text-purple-800">Capability Transfer Rate</p>
            <p className="text-xs text-purple-600">How much learning transfers to job performance</p>
          </div>
          {transferMethod && (
            <span
              className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                transferMethod === 'roe_composite'
                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                  : 'bg-amber-100 text-amber-700 border border-amber-200'
              }`}
              data-testid="transfer-method-badge"
            >
              {transferMethod === 'roe_composite' ? 'Measured (ROE)' : 'Estimated'}
            </span>
          )}
        </div>
        <div className="text-right">
          {transferMethod === 'roe_composite' && roeCompositeDetail ? (
            <>
              <p className="text-2xl font-bold text-emerald-700" data-testid="roe-composite-rate">
                {(roeCompositeDetail.factor * 100).toFixed(1)}%
              </p>
              <p className="text-xs text-emerald-500">ROE Composite</p>
            </>
          ) : (
            <>
              <p className="text-2xl font-bold text-purple-700">
                {transferRate.transfer_rate_percent}%
              </p>
              <p className="text-xs text-purple-500">
                Base {transferRate.base_rate}% {transferRate.total_adjustment >= 0 ? '+' : ''}{transferRate.total_adjustment}%
              </p>
            </>
          )}
        </div>
      </div>
      
      {/* ROE Composite Breakdown (when measured) */}
      {transferMethod === 'roe_composite' && roeCompositeDetail?.components && (
        <div className="grid grid-cols-3 gap-2 text-xs mb-3" data-testid="roe-composite-breakdown">
          {Object.entries(roeCompositeDetail.components).map(([key, comp]) => {
            const labels = { roe_l2: 'L2 Effectiveness', roe_motivation: 'Motivation', roe_l3: 'L3 Behavior Transfer' };
            return (
              <div key={key} className="p-2 bg-white/60 rounded border border-emerald-100">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">{labels[key] || key}</span>
                  <span className="font-medium text-emerald-700">{(comp.contribution * 100).toFixed(1)}%</span>
                </div>
                <p className="text-slate-400 text-[10px] mt-0.5">
                  Score {comp.score} × {(comp.weight * 100).toFixed(0)}% weight
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Synthetic Factor Breakdown (when estimated) */}
      {transferMethod !== 'roe_composite' && (
      <div className="grid grid-cols-3 gap-2 text-xs">
        {Object.entries(transferRate.factors).map(([key, factor]) => (
          <div key={key} className="p-2 bg-white/60 rounded border border-purple-100">
            <div className="flex justify-between items-center">
              <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
              <span className={`font-medium ${factor.points >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                {factor.points >= 0 ? '+' : ''}{factor.points}%
              </span>
            </div>
            <p className="text-slate-400 text-[10px] mt-0.5 truncate" title={factor.description}>
              {factor.description}
            </p>
          </div>
        ))}
      </div>
      )}
    </div>
  );
};

export default TransferFactorsCard;
